import { Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';


import { NotificationService } from '@core/api/notification.service';
import { UserService } from '@core/api/user.service';
import { User } from '@shared/models';
import { mobileViewMaxScreenWidth, setMobileViewMode } from '@store/app-config/app-config.actions';
import { login, setActAsUser } from '@store/auth/auth.actions';
import { AuthState } from '@store/auth/auth.reducer';
import { selectAuthState, selectCurrentUser } from '@store/auth/auth.selectors';
import * as fromApp from './store/index';
import { Meta, Title } from "@angular/platform-browser";
import { AppConfigService } from '@core/api/app-config.service';
import { DialogYesNoComponent } from "@shared/dialogs/dialog-yes-no/dialog-yes-no.component";
import { MatDialog } from "@angular/material/dialog";
import { environment } from '@env/environment';

declare let gtag: Function;
declare global {
  interface Window {
    CobrowseIO: any;
  }
}

@Component({
  selector: 'fc-root',
  styleUrls: ['./app.component.scss'],
  templateUrl: `./app.component.html`,
  standalone: false
})
export class AppComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  currentUserId: number = null;
  cobrowseStarted: boolean = false;
  cobrowseSetup: boolean = false;

  constructor(
    public dialog: MatDialog,
    readonly store: Store<fromApp.AppState>,
    readonly route: ActivatedRoute,
    readonly appConfigService: AppConfigService,
    readonly router: Router,
    readonly userService: UserService,
    readonly notificationService: NotificationService,
    readonly meta: Meta,
    readonly titleService: Title
  ) {
    // Add Google Analytics to index.html
    if ('googleAnalyticsID' in environment) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${environment.googleAnalyticsID}`;
      document.head.appendChild(script);
    }
    this.onResize();

    let zendeskId = this.appConfigService.getValue('zendesk_bot_id');
    if (zendeskId) {
      const zdScript = document.createElement('script');
      zdScript.async = true;
      zdScript.id = 'ze-snippet';
      zdScript.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskId}`;
      document.head.prepend(zdScript);
    }
    let coBrowseKey = this.appConfigService.getValue('co_browse_io_key');
    if (coBrowseKey) {
      window.CobrowseIO = {
        license: coBrowseKey,
        client: () => {
          return new Promise(resolve => {
            const script = document.createElement('script');
            script.src = 'https://js.cobrowse.io/CobrowseIO.js';
            script.async = true;
            script.crossOrigin = 'anonymous';

            script.onload = () => resolve(window.CobrowseIO);
            document.head.appendChild(script);
          });
        }
      };
      this.cobrowseSetup = true;
    }

    this.router.events
      .pipe(
        filter((evt: any) => evt instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.titleService.setTitle('Forest Land Platform for Carbon Revenue - LandYield');
        this.meta.updateTag({
          name: 'description',
          content: 'Use LandYield\'s free platform to design your project and estimate your potential earnings through our carbon credit program.'
        });
        if ('googleAnalyticsID' in environment) {
          gtag('config', environment.googleAnalyticsID);
        }
      });
  }

  cobrowsePopup(target: any, method: string, dialogData: any) {
    target[method] = () => {
      if (this.dialog.openDialogs.length > 0) {
        return;
      }
      return new Promise((resolve, reject) => {
        this.dialog.open(DialogYesNoComponent, dialogData)
          .afterClosed()
          .subscribe((result: boolean) => {
            if (result) {
              resolve(true);
            } else {
              reject('User Declined');
            }
          });
      });
    };
  }

  ngOnInit() {
    this.store
      .select(selectAuthState)
      .pipe(take(1))
      .subscribe((authState: AuthState) => {
        if (authState.token) {
          const initialPath = window.location.pathname + window.location.search;
          this.store.dispatch(
            login({
              data: {
                idToken: authState.token,
                tokenExp: authState.exp,
                returnUrl: initialPath,
              },
            })
          );
          if (authState.actAsUser) {
            this.store.dispatch(setActAsUser({ data: new User(authState.actAsUser) }));
          }
        }
      });
    this.store.select(selectCurrentUser)
      .pipe(distinctUntilChanged((a, b) => a?.id === b?.id)).subscribe(currentUser => {

      if (!currentUser && this.cobrowseStarted) {
        window.CobrowseIO.client().then(() => {
          window.CobrowseIO.stop();
          this.cobrowseStarted = false;
          this.currentUserId = null;
        });
        return;
      }

      if (currentUser?.id === this.currentUserId) {
        return;
      }

      if (currentUser && this.cobrowseSetup) {
        const baseDialogData = {
          panelClass: 'fc-yes-no-dialog',
          autoFocus: false,
          data: {
            buttonTextYes: 'Yes, Allow',
            buttonTextNo: 'No, Deny',
          },
          line2: 'You can stop sharing your screen at any time.'
        };
        this.cobrowsePopup(window.CobrowseIO, 'confirmSession', {
          ...baseDialogData,
          data: {
            ...baseDialogData.data,
            title: 'Request from LandYield – View Screen',
            line1:
              'A LandYield agent would like to view your screen to provide assistance.',
          }
        });
        this.cobrowsePopup(window.CobrowseIO, 'confirmRemoteControl', {
          ...baseDialogData,
          data: {
            ...baseDialogData.data,
            title: 'Request from LandYield – Make Updates',
            line1:
              'The LandYield agent viewing your screen would like to control your mouse to make updates.',
          }
        });
        this.cobrowsePopup(window.CobrowseIO, 'confirmFullDevice', {
          ...baseDialogData,
          data: {
            ...baseDialogData.data,
            title: 'Request from LandYield – See More Sites',
            line1:
              'To provide further assistance, your LandYield agent needs to view additional sites.',
          }
        });

        window.CobrowseIO.client().then(() => {

          // Stop any existing session
          if (this.cobrowseStarted) {
            window.CobrowseIO.stop();
          }

          // Optional session metadata
          window.CobrowseIO.customData = {
            device_name: currentUser.getDisplayName(),
            id: currentUser.id,
            name: currentUser.getDisplayName(),
            email: currentUser.email
          };

          window.CobrowseIO.start();

          this.cobrowseStarted = true;
          this.currentUserId = currentUser.id;
        });
      }
    });


    this.notificationService.initNotificationChecks();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.store.dispatch(
      setMobileViewMode({
        mobileViewMode: window.innerWidth <= mobileViewMaxScreenWidth,
      })
    );
  }
}
